import { useEffect } from "react";

const SITE_ORIGIN = "https://geminii-ai.com";
const DEFAULT_IMAGE = "/images/hero-home.png";
const DEFAULT_TITLE = "Geminii — Software, Hardware, Intelligence";
const DEFAULT_DESC =
  "Software, hardware, intelligence — built for the operators who run the real world.";

// Per-route default OG image. Override with the `image` prop on the page.
const ROUTE_IMAGE = [
  { match: (p) => p === "/space" || p.startsWith("/space/rogue/pricing"), image: "/images/hero-space.png" },
  { match: (p) => p.startsWith("/space/rogue"), image: "/images/hero-rogue.png" },
  { match: (p) => p.startsWith("/space"), image: "/images/hero-space.png" },
  { match: (p) => p.startsWith("/cerebrum"), image: "/images/hero-cerebrum.png" },
  { match: (p) => p.startsWith("/nexus"), image: "/images/hero-rogue.png" },
  { match: (p) => p === "/", image: "/images/hero-home.png" },
];

const pickRouteImage = (path) => {
  for (const rule of ROUTE_IMAGE) if (rule.match(path)) return rule.image;
  return DEFAULT_IMAGE;
};

// Always-on Organization + WebSite schema. Injected on every page.
const GLOBAL_SCHEMAS = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_ORIGIN}#organization`,
    name: "Geminii",
    alternateName: "Geminii Space",
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/images/geminii-logo.png`,
    sameAs: [
      "https://github.com/geminii-ai",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@geminii-ai.com",
      availableLanguage: ["en"],
    },
    description: DEFAULT_DESC,
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}#website`,
    name: "Geminii",
    url: SITE_ORIGIN,
    publisher: { "@id": `${SITE_ORIGIN}#organization` },
    inLanguage: "en",
  },
];

export default function PageMeta({ title, description, path, image, jsonLd }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | Geminii` : DEFAULT_TITLE;
    document.title = fullTitle;

    const setMeta = (key, content) => {
      const isOgOrTwitter = key.startsWith("og:") || key.startsWith("twitter:");
      const selector = isOgOrTwitter
        ? `meta[property="${key}"], meta[name="${key}"]`
        : `meta[name="${key}"]`;
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute("content", content);
      } else {
        el = document.createElement("meta");
        if (isOgOrTwitter) el.setAttribute("property", key);
        else el.setAttribute("name", key);
        el.setAttribute("content", content);
        document.head.appendChild(el);
      }
    };

    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const cleanPath = path || "";
    const url = `${SITE_ORIGIN}${cleanPath}`;
    const desc = description || DEFAULT_DESC;
    const imagePath = image || pickRouteImage(cleanPath);
    const imageUrl = imagePath.startsWith("http") ? imagePath : `${SITE_ORIGIN}${imagePath}`;

    // Core
    setMeta("description", desc);
    setCanonical(url);

    // Open Graph
    setMeta("og:title", fullTitle);
    setMeta("og:description", desc);
    setMeta("og:url", url);
    setMeta("og:type", "website");
    setMeta("og:site_name", "Geminii");
    setMeta("og:image", imageUrl);
    setMeta("og:image:width", "1280");
    setMeta("og:image:height", "896");
    setMeta("og:image:alt", `${title || "Geminii"} — preview`);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", imageUrl);
    setMeta("twitter:image:alt", `${title || "Geminii"} — preview`);

    // JSON-LD structured data
    // Strip any previously-injected schema on this route, then re-inject.
    document.querySelectorAll('script[data-pagemeta-jsonld]').forEach((n) => n.remove());

    const allSchemas = [
      ...GLOBAL_SCHEMAS,
      ...(Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : [])),
    ];
    allSchemas.forEach((schema, i) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-pagemeta-jsonld", String(i));
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    });

    return () => {
      // Cleanup on unmount so the next page starts clean
      document.querySelectorAll('script[data-pagemeta-jsonld]').forEach((n) => n.remove());
    };
  }, [title, description, path, image, jsonLd]);

  return null;
}
